import { SAV_KIND_VALUES, SAV_SORT_KEYS, SAV_STATUS_VALUES } from "@erp/shared";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { Clock, LifeBuoy, Plus } from "lucide-react";
import { useCallback } from "react";
import { z } from "zod";
import { Badge } from "~/components/ui/badge";
import { buttonVariants } from "~/components/ui/button";
import { DataList, type Column } from "~/components/ui/data-list";
import { Page, PageHeader, Panel, SearchInput, Toolbar } from "~/components/ui/page";
import { Select } from "~/components/ui/select";
import { EmptyState, ErrorState } from "~/components/ui/states";
import { isStale, KIND_TONE, SAV_TONE } from "~/features/sav/status";
import { api, unwrap } from "~/lib/api";
import { cn } from "~/lib/cn";
import { useFormat, useT } from "~/lib/i18n";
import { useCan } from "~/lib/session";
import { useSavReasons } from "~/features/sav/reasons";
import { useInfiniteList } from "~/lib/use-list";

const VIEWS = ["active", ...SAV_STATUS_VALUES] as const;
const ALL = "all";
const search = z.object({
  q: z.string().optional(),
  status: z.enum(VIEWS).optional(),
  kind: z.enum(SAV_KIND_VALUES).optional(),
  sort: z.enum(SAV_SORT_KEYS).optional(),
  dir: z.enum(["asc", "desc"]).optional(),
});
type Search = z.infer<typeof search>;

export const Route = createFileRoute("/_app/sav/")({ validateSearch: search, component: SavList });

function fetchTickets(params: Search & { offset: number; limit: number }) {
  return unwrap(api.sav.get({ query: { ...params, status: params.status ?? "active" } }));
}
type Row = Awaited<ReturnType<typeof fetchTickets>>["items"][number];

const chip = (active: boolean) =>
  cn(
    "flex h-9 shrink-0 items-center gap-2 rounded-full border px-3.5 text-sm font-medium transition-colors",
    active
      ? "border-ink bg-ink text-white dark:border-gold dark:bg-gold dark:text-gold-ink"
      : "border-line-strong bg-surface text-fg-2 hover:text-fg",
  );

function Age({ row }: { row: Row }) {
  const t = useT();
  const format = useFormat();
  const stale = isStale(row.status, row.createdAt);
  return (
    <span
      className={cn("inline-flex items-center gap-1", stale ? "font-medium text-danger" : "text-fg-2")}
      title={stale ? t("sav.hints.stale") : undefined}
    >
      {stale ? <Clock className="size-3.5" aria-hidden /> : null}
      {format.relative(row.createdAt)}
    </span>
  );
}

function SavList() {
  const t = useT();
  const reasons = useSavReasons();
  const can = useCan();
  const navigate = useNavigate({ from: Route.fullPath });
  const filters = Route.useSearch();
  const view = filters.status ?? "active";
  const list = useInfiniteList("sav", filters, fetchTickets);
  const setSearch = useCallback(
    (patch: Partial<Search>) => void navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true }),
    [navigate],
  );

  const columns: Column<Row>[] = [
    {
      id: "number",
      header: t("sav.fields.number"),
      sortKey: "number",
      cell: (r) => <span className="font-medium tabular">{r.number}</span>,
    },
    {
      id: "customer",
      header: t("sav.fields.customer"),
      sortKey: "customer",
      cell: (r) => (
        <div className="min-w-0 py-1.5">
          <p className="truncate">{r.customerName}</p>
          <p className="truncate text-xs text-fg-3">{r.subject}</p>
        </div>
      ),
      className: "max-w-[320px]",
    },
    {
      id: "kind",
      header: t("sav.fields.kind"),
      cell: (r) => <Badge tone={KIND_TONE[r.kind]}>{t(`sav.kinds.${r.kind}`)}</Badge>,
      minWidth: "lg",
    },
    {
      id: "reason",
      header: t("sav.fields.reason"),
      cell: (r) => reasons.label(r.reason),
      className: "text-fg-2",
      minWidth: "lg",
    },
    {
      id: "order",
      header: t("sav.fields.order"),
      cell: (r) => <span className="tabular text-fg-2">{r.orderNumber ?? ""}</span>,
      minWidth: "xl",
    },
    // Sorted by opening date: « newest first » shows the youngest tickets first.
    { id: "age", header: t("sav.fields.age"), sortKey: "createdAt", cell: (r) => <Age row={r} /> },
    {
      id: "status",
      header: t("common.fields.status"),
      sortKey: "status",
      cell: (r) => <Badge tone={SAV_TONE[r.status]}>{t(`sav.statuses.${r.status}`)}</Badge>,
    },
  ];

  return (
    <Page>
      <PageHeader
        title={t("sav.title")}
        actions={
          can("sav:write") ? (
            <Link to="/sav/new" className={buttonVariants({ variant: "primary" })}>
              <Plus />
              {t("sav.new")}
            </Link>
          ) : null
        }
      />
      <div className="no-scrollbar mb-3 flex gap-2 overflow-x-auto">
        {VIEWS.map((v) => (
          <button
            key={v}
            type="button"
            aria-pressed={view === v}
            onClick={() => setSearch({ status: v === "active" ? undefined : v })}
            className={chip(view === v)}
          >
            {t(`sav.views.${v}`)}
          </button>
        ))}
      </div>
      <Panel>
        <Toolbar>
          <SearchInput
            value={filters.q ?? ""}
            onChange={(q) => setSearch({ q: q || undefined })}
            placeholder={t("sav.searchPlaceholder")}
          />
          <div className="w-full sm:ml-auto sm:w-44">
            <Select
              aria-label={t("sav.fields.kind")}
              value={filters.kind ?? ALL}
              onValueChange={(v) => setSearch({ kind: v === ALL ? undefined : (v as Search["kind"]) })}
              options={[
                { value: ALL, label: t("sav.allKinds") },
                ...SAV_KIND_VALUES.map((k) => ({ value: k, label: t(`sav.kinds.${k}`) })),
              ]}
            />
          </div>
        </Toolbar>
        {list.isError ? (
          <ErrorState error={list.error} onRetry={() => void list.refetch()} />
        ) : (
          <DataList
            rows={list.rows}
            rowKey={(r) => r.id}
            columns={columns}
            card={(r) => (
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="truncate font-medium">{r.customerName}</p>
                  <p className="truncate text-sm text-fg-2">{r.subject}</p>
                  <p className="mt-0.5 text-xs text-fg-3 tabular">
                    {r.number} — {t(`sav.kinds.${r.kind}`)} — {reasons.label(r.reason)}
                  </p>
                </div>
                <div className="flex shrink-0 flex-col items-end gap-1 text-sm">
                  <Badge tone={SAV_TONE[r.status]}>{t(`sav.statuses.${r.status}`)}</Badge>
                  <Age row={r} />
                </div>
              </div>
            )}
            onOpen={(r) => void navigate({ to: "/sav/$id", params: { id: r.id } })}
            sort={{ key: filters.sort ?? "createdAt", dir: filters.dir ?? "desc" }}
            onSortChange={(s) => setSearch({ sort: s.key as Search["sort"], dir: s.dir })}
            loading={list.isLoading}
            hasMore={list.hasMore}
            loadingMore={list.loadingMore}
            onLoadMore={list.loadMore}
            isDimmed={(r) => r.status === "closed"}
            empty={
              view === "active" && !filters.q && !filters.kind ? (
                <EmptyState icon={<LifeBuoy />} title={t("sav.empty")} body={t("sav.emptyBody")} />
              ) : (
                <EmptyState title={t("common.states.noResults")} body={t("common.states.noResultsHint")} />
              )
            }
          />
        )}
      </Panel>
    </Page>
  );
}
